import { intersection, uniq, max, min, clamp } from 'lodash';
import { generateId } from '../../../shared/utils/generate-id';
import { isRoughlyEqual } from '../../utils/math';
import { MINUTE, SECOND } from '../../utils/time';
import { UpdateState } from '../reducers/update.reducer';
import { ShrInterval } from '../types/interval.type';
import { TagLabel } from '../types/tag.type';
import {
  INVALID_WAVE_TYPES,
  InvalidWaveType,
  ServerWave,
  ServerWaveType,
  Wave,
  WaveType,
  WaveTypeMapping,
} from '../types/wave.type';
import { DELINEATION_LABELS, getOptionalTags } from './tags.utils';

export const invalidTypes: InvalidWaveType[] = [...INVALID_WAVE_TYPES];

export const DEFAULT_WAVE_LENGTH: WaveTypeMapping<number> = {
  P: 80,
  QRS: 100,
  T: 200,
  artifact: 500,
  calibration: 500,
  noise: 500,
  no_signal: 500,
  p_level_noise: 500,
  qrs_level_noise: 500,
  pt_level_noise: 500,
  uncertain: 500,
  microvolted: 500,
};

export const MINIMUM_LINKED_WAVE_LENGTH = 40; // ms
export const SNAP_THRESHOLD = 120; // ms

/**
 *
 * Sometimes clients add qrs waves on top of already existing qrs waves by mistake
 * these need to be filtered on the calculation of the shr otherwise it's going to bring false results
 */
export const removeOverlappingWaves = (waves: Wave[]) => {
  return waves
    .sort((a, b) => a.onset - b.onset)
    .filter((wave, i, waveArray) => {
      const previousWave = waveArray[i - 1];

      if (previousWave == null) {
        return true;
      }

      if (
        wave.offset <= previousWave.offset ||
        previousWave.offset >= wave.onset
      ) {
        return false;
      }

      return true;
    });
};

/**
 * compute SHR for each beat
 * - the first 6 beats shr is ignored
 * - If in the span of 10 seconds there are less than 2 beats the shr is ignored
 * - If there are more than 12 beats in the span of 10 seconds we take last 12 beats,
 * otherwise use all beats within those 10 seconds
 */
export const createShrInterval = (waves: Wave[]): ShrInterval[] => {
  const qrsWaves = waves.filter((wave) => wave.type === 'QRS');
  const uniqueWaves = removeOverlappingWaves(qrsWaves);
  // to the invalid shr values we add 0 which is not feasible in order to use it
  // for the calculation of the primary labels Ventricular Rhythm and Ventricular Tachy
  // but will be removed in the selector which produces the interval shr axis values
  return uniqueWaves.map((wave, index) => {
    if (index < 6) {
      return {
        timestamp: 1,
        type: 'invalid',
        pvc: wave.tags.includes('ectopic') && wave.tags.includes('premature'),
      };
    }
    const startingIndex = index - 11 < 0 ? 0 : index - 11;

    const wavesToUse = getWWavesToUseForSHR(
      wave,
      uniqueWaves.slice(startingIndex, index + 1),
    );
    if (wavesToUse.length < 2) {
      return {
        timestamp: 1,
        type: 'invalid',
        pvc: wave.tags.includes('ectopic') && wave.tags.includes('premature'),
      };
    }

    const shr = computeSHR(wavesToUse);
    if (shr == null) {
      return {
        timestamp: 1,
        type: 'invalid',
        pvc: wave.tags.includes('ectopic') && wave.tags.includes('premature'),
      };
    }
    const previousWave = wavesToUse[wavesToUse.length - 2];

    return {
      timestamp: (wave.onset + previousWave.onset) / 2,
      shr,
      type: 'valid',
      pvc: wave.tags.includes('ectopic') && wave.tags.includes('premature'),
    };
  });
};

export const getWWavesToUseForSHR = (
  selectedWave: Wave,
  waves: Wave[],
): Wave[] => {
  const minOnset = selectedWave.onset - 10 * SECOND;

  const filteredWaves = waves.filter(({ onset }) => onset >= minOnset);

  return filteredWaves;
};

export const computeSHR = (waves: Wave[]): number | null => {
  // we compute the shr for a noisy qrs as usual. But if its one of the preceding qrs's then
  // we do not include it in the sum of waves we will use for the final
  // formula ((sumOfWaves * MINUTE) / duration) nor we add its distance from its preceding qrs (for the calculation of
  // the duration).
  const duration = waves.reduce((duration, wave, index, waves) => {
    // dont add any duration distance if:
    // (1) it's the first qrs of the preceding ones (because it doesn't have a previous)
    // (2) it's a noisy qrs
    if (
      waves[index - 1] == null ||
      wave.tags.includes(DELINEATION_LABELS.NOISY_RR.short)
    ) {
      return duration + 0;
    }

    return duration + (wave.onset - waves[index - 1].onset);
  }, 0);

  // we always exclude the first one because it is the beginning and we want to even out
  // the distances with the number of waves
  const finalWaves = waves
    .slice(1)
    .filter(({ tags }) => !tags.includes(DELINEATION_LABELS.NOISY_RR.short));

  // avoid having a NaN result
  if (finalWaves.length < 1 || duration == null) {
    return null;
  }

  return Math.round((finalWaves.length * MINUTE) / duration);
};

export const isWaveManual = (wave: Wave): boolean =>
  wave.tags.includes('manual');

export const getWaveDuration = (wave: Wave) => wave.offset - wave.onset;

export const sortWaves = (a: Wave, b: Wave) =>
  isRoughlyEqual(a.onset, b.onset) ? a.offset - b.offset : a.onset - b.onset;

export const getUpdatedWave = (
  wave: Wave,
  update: UpdateState,
  ecgDuration: number,
  linkedWave?: Wave,
): Wave => {
  if (update.id == null) {
    return wave;
  }

  let correctedOnset = update.onset;
  let correctedOffset = update.offset;
  switch (update.id) {
    case wave.id: {
      if (wave.link && linkedWave && update.updateMode === 'boundary') {
        if (wave.type === 'QRS' && update.boundary === 'onset') {
          correctedOnset = min([
            correctedOnset,
            wave.offset - wave.onset - MINIMUM_LINKED_WAVE_LENGTH,
          ]) as number;
        } else if (wave.type === 'T') {
          correctedOffset = max([
            correctedOffset,
            wave.onset - wave.offset + MINIMUM_LINKED_WAVE_LENGTH,
          ]) as number;
          correctedOnset = clamp(
            correctedOnset,
            linkedWave.onset - wave.onset + MINIMUM_LINKED_WAVE_LENGTH,
            wave.offset - wave.onset - MINIMUM_LINKED_WAVE_LENGTH,
          );
        }
      }
      return correctWave(wave, correctedOnset, correctedOffset, ecgDuration);
    }
    case wave.link?.waveId: {
      if (
        update.updateMode === 'move' ||
        (update.updateMode === 'boundary' &&
          update.boundary === 'onset' &&
          wave.type === 'QRS')
      ) {
        if (update.updateMode === 'boundary' && linkedWave) {
          correctedOnset = 0;
          correctedOffset = clamp(
            update.onset,
            wave.onset - wave.offset + MINIMUM_LINKED_WAVE_LENGTH,
            linkedWave.offset - linkedWave.onset - MINIMUM_LINKED_WAVE_LENGTH,
          );
        }
        return correctWave(wave, correctedOnset, correctedOffset, ecgDuration);
      }

      return wave;
    }
    default:
      return wave;
  }
};

export const isWaveOfType = (wave: Wave, ...types: WaveType[]): boolean => {
  return types.some((type) => wave.type === type);
};

export const correctWave = (
  wave: Wave,
  updateOnset: number,
  updateOffset: number,
  ecgDuration?: number,
): Wave => {
  const updatedOnset = wave.onset + updateOnset;
  const updatedOffset = wave.offset + updateOffset;
  let onset = Math.min(updatedOnset, updatedOffset);
  let offset = Math.max(updatedOnset, updatedOffset);
  if (isWaveOfType(wave, ...INVALID_WAVE_TYPES)) {
    onset = onset < SNAP_THRESHOLD ? 0 : onset;
    if (ecgDuration != null) {
      offset = offset > ecgDuration - SNAP_THRESHOLD ? ecgDuration : offset;
    }
  }

  return {
    ...wave,
    onset,
    offset,
  };
};

export const getWaveLeads = (wave: Wave, leads: string[]): string[] => {
  switch (wave.type) {
    case 'calibration':
    case 'artifact':
    case 'noise':
    case 'no_signal':
    case 'p_level_noise':
    case 'qrs_level_noise':
    case 'pt_level_noise':
    case 'uncertain':
    case 'microvolted': {
      const foundLeads = intersection(wave.tags, leads);
      return foundLeads.length > 0 ? foundLeads : leads;
    }
    default: {
      return leads;
    }
  }
};

export const getWaveType = (wave: ServerWave): WaveType =>
  getTypeFromTags(wave.type, wave.tags);

export const getTypeFromTags = (
  waveType: ServerWaveType | WaveType,
  tags: TagLabel[],
): WaveType => {
  switch (waveType) {
    case 'calibration':
    case 'artifact':
    case 'noise':
    case 'no_signal':
    case 'p_level_noise':
    case 'qrs_level_noise':
    case 'pt_level_noise':
    case 'uncertain':
    case 'microvolted':
    case 'INV': {
      const invalidTags = intersection(
        tags as InvalidWaveType[], // we can cast because invalidTypes
        // has this type and so will the intersection
        invalidTypes,
      );

      return invalidTags[0] || 'artifact'; // we try to find a type, and default to artifact
    }
    default: {
      return waveType;
    }
  }
};

export const generateWaveId = () => generateId('wave');

export const appendTags = (
  ...tags: Array<TagLabel | TagLabel[]>
): TagLabel[] => {
  return uniq(tags.flatMap((tag) => tag));
};

export const includeManual = (tagLabels: TagLabel[]): TagLabel[] => {
  return appendTags(tagLabels, 'manual');
};

export const toManualWave = (wave: Wave): Wave => {
  return { ...wave, tags: includeManual(wave.tags) };
};

export const sortWavesByLayers = (waves: Wave[]) => {
  const background: Wave[] = [];
  const foreground: Wave[] = [];

  waves.forEach((wave) => {
    if (wave.background) {
      background.push(wave);
    } else {
      foreground.push(wave);
    }
  });

  return [background, foreground] as const;
};

export const copyWave = (wave: Wave, extra: Partial<Wave> = {}): Wave => {
  return {
    ...wave,
    id: generateWaveId(),
    ...extra,
  };
};

export const getWavesUntilTime = (waves: Wave[], time: number) =>
  waves.filter((wave) => wave.offset < time);

export const getLinkedWaves = (waves: Wave[]): Wave[] => {
  const sortedWaves = waves.slice().sort(sortWaves);
  const linkedWaves: Wave[] = [];

  for (let i = 0; i < sortedWaves.length; i += 1) {
    const wave = { ...sortedWaves[i] };
    linkedWaves.push(wave);

    if (wave.type === 'T') {
      const previousWaves = linkedWaves.slice(0, i);

      for (let j = previousWaves.length - 1; j >= 0; j -= 1) {
        const possibleMatch = previousWaves[j];

        if (possibleMatch.type === 'T') {
          break;
        } else if (possibleMatch.type === 'QRS') {
          wave.link = { type: 'onset', waveId: possibleMatch.id };
          possibleMatch.link = { type: 'offset', waveId: wave.id };
          break;
        }
      }
    }
  }

  return linkedWaves;
};

/**
 * Returns waves that FAIL the optional tag requirement number.
 * numberOfTagsRequired:
 *  - 0 (default): wave must include at least 1 of the optionalTagList. Returned if it has 0.
 *  - number (e.g. 2): wave must include exactly that many of the optionalTagList. Returned if optional tag count !== number.
 */
export const getWavesFailingOptionalTagRequirement = (
  waveList: Wave[],
  optionalTagList: string[],
  numberOfTagsRequired = 0,
) => {
  return waveList.filter((wave) => {
    const waveOptionalTags = getOptionalTags(wave.tags).filter((t) =>
      optionalTagList.includes(t),
    );
    const optionalTagCount = waveOptionalTags.length;

    if (numberOfTagsRequired === 0) {
      return optionalTagCount < 1;
    } else {
      return optionalTagCount !== numberOfTagsRequired;
    }
  });
};
